/**
 * NebulaField — what a nebula IS, before anything is drawn (space realism, 2026-10-07). PURE: no three, no DOM.
 *
 * A nebula is described by a `NebulaSpec` (one of five physical KINDS + a seed) and baked ONCE into a small volume:
 *
 *   R  gas    — ionisable gas density 0..1 (line emission goes with gas², recombination)
 *   G  light  — the fraction of the star's light that reaches this point, exp(−τ) along the ray FROM THE STAR: an
 *               ionisation front is where dense gas meets that light, and whatever stands behind a dense clump is in
 *               its shadow (the pillars of a real H II region are exactly this)
 *   B  dust   — dust density 0..1 (it absorbs — reddening what is behind — and scatters the star's light, blue)
 *   A  h      — a nebulaSmooth wrinkle field for the SHEETS: a remnant's filaments and a planetary nebula's rims are thin
 *               shells |p| = R + amp·h seen edge-on, integrated analytically by the march (NebulaShade)
 *
 * The kinds (see `nebulaPreset`):
 *   emission   — an H II region: a hot star has blown a cavity in a molecular cloud; the cavity's wall glows where the
 *                star lights it, fingers of dense gas point back at the star, dust lanes cross the glow
 *   reflection — dust beside a bright star: no line emission, blue scattered starlight in streaked wisps
 *   dark       — a dense dust cloud in front of thin glowing gas lit by a star outside it: a silhouette with a bright rim
 *   planetary  — the shells a dying star threw off: a bright ring, teal inside, red at the rim, a faint outer halo
 *   remnant    — a supernova's shock front: nothing but thin wrinkled sheets, which the eye reads as filaments
 *
 * `NebulaBaker` does the work in slices (`step(budgetMs)`), so a caller can spread ~1 s of arithmetic over idle frames.
 * The light is integrated ONCE per direction from the star (an octahedral table of optical depth by distance), never
 * per voxel — the same radial structure the real thing has, and it keeps a shadow's edge as sharp as the grid.
 */

export type NebulaV3 = [number, number, number];
export type NebulaRGB = [number, number, number];
export type NebulaKind = 'emission' | 'reflection' | 'dark' | 'planetary' | 'remnant';
export const NEBULA_KINDS: readonly NebulaKind[] = ['emission', 'reflection', 'dark', 'planetary', 'remnant'];

/** The star that lights the nebula: a POINT inside or near it (unit-sphere coordinates) or a DIRECTION to one far outside. */
export interface NebulaLight { mode: 'point' | 'dir'; pos: NebulaV3 }

/** A star drawn inside the volume (its light is dimmed by the dust in front of it, not by the dust behind it). */
export interface NebulaStar { pos: NebulaV3; color: NebulaRGB; power: number }

/**
 * A thin emissive SHELL: the surface |p ⊘ axes| = radius + amp·h(perm(p)), `width` thick. Its leading edge (outside)
 * glows `colorOut`, its trailing edge `colorIn`, `split` apart — a shock's [O III] runs ahead of its Hα.
 */
export interface NebulaSheet {
  axes: NebulaV3; radius: number; amp: number; width: number; split: number;
  /** Which axis permutation of p the wrinkle field is read at (0..5) — sheets must not share their wrinkles. */
  perm: number;
  colorOut: NebulaRGB; colorIn: NebulaRGB; power: number;
  /** The sheet shows where the gas channel is between these (patchiness); 0 / 0 = everywhere. */
  maskLo: number; maskHi: number;
  /** Fine wrinkles the march adds from its detail volume (radii): what turns one filament into a rope of them. */
  ripple?: number;
  /**
   * Edge-on gain. A thin sheet's brightness goes as 1/|cos θ| of the view to its normal, up to √(curvature radius /
   * pixel) at a fold — which a photograph resolves 20× finer than a game frame does. `sharpen` γ > 1 draws
   * (1/|cos θ|)^γ, giving back the fold-to-face contrast the pixel took away; 1 = the exact thin sheet.
   */
  sharpen?: number;
}

export interface NebulaSpec {
  kind: NebulaKind;
  seed: number;
  light: NebulaLight | null;
  // ── the look: every number below is a uniform of the march ──
  /** Low-ionisation line emission (Hα + Hβ, [N II], [S II]): the rose red of an H II region. */
  colLow: NebulaRGB;
  /** High-ionisation line emission ([O III]): teal. */
  colHigh: NebulaRGB;
  /** Emissivity per unit length at gas = 1, fully lit. */
  emission: number;
  /** The high-ionisation line's strength relative to the low one where both exist. */
  highGain: number;
  /** The high-ionisation zone by distance from the star: whole inside hiR0, gone by hiR1 … */
  hiR0: number; hiR1: number;
  /** … and it needs light^hiPow (the harder photons are used up first). */
  hiPow: number;
  /** The star's colour and strength as the dust scatters it. */
  starColor: NebulaRGB; starPower: number;
  /** Dust albedo tint (bluer than the star: small grains scatter blue). */
  colScatter: NebulaRGB;
  scatter: number;
  /** Henyey–Greenstein asymmetry of the dust (forward-scattering). */
  phaseG: number;
  /** The visible starlight on dust is light^lightPow (the dust is clearer in the visible than in the ionising UV). */
  lightPow: number;
  /** Dust lit by the galaxy's general starlight ("cloudshine"): tiny. */
  ambient: NebulaRGB;
  /** Dust extinction per unit length at dust = 1 (green), and its ratio per channel (R, G, B) — the reddening law. */
  kappa: number; redden: NebulaRGB;
  /** A small grey self-absorption of the gas, so a dense core never adds without bound. */
  gasKappa: number;
  /** Optical depth of the star's light per unit length at gas = 1 / dust = 1 (the bake). */
  kIon: number; kIonDust: number;
  sheets: NebulaSheet[];
  stars: NebulaStar[];
  /** Small-scale wisps the march adds on top of the baked volume: strength 0..1, cycles per radius. */
  detail: number; detailFreq: number;
  /** Kind-specific shape numbers (documented at each kind's field). */
  shape: Record<string, number>;
}

// ─── noise ───────────────────────────────────────────────────────────────────────────────────────────────────────────

function mulberry32(a: number): () => number {
  let s = a >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const fade = (t: number): number => t * t * t * (t * (t * 6 - 15) + 10);
function grad(h: number, x: number, y: number, z: number): number {
  const g = h & 15;
  const u = g < 8 ? x : y;
  const v = g < 4 ? y : g === 12 || g === 14 ? x : z;
  return ((g & 1) === 0 ? u : -u) + ((g & 2) === 0 ? v : -v);
}

/** Seeded Perlin gradient noise and the sums built on it. One instance per nebula (the seed is the nebula). */
export class NebulaNoise {
  private readonly p = new Uint8Array(512);
  constructor(seed: number) {
    const r = mulberry32((seed >>> 0) || 1);
    const t = new Uint8Array(256);
    for (let i = 0; i < 256; i++) t[i] = i;
    for (let i = 255; i > 0; i--) { const j = Math.floor(r() * (i + 1)); const s = t[i]; t[i] = t[j]; t[j] = s; }
    for (let i = 0; i < 512; i++) this.p[i] = t[i & 255];
  }
  /** Gradient noise, about −1..1. */
  n(x: number, y: number, z: number): number {
    let X = Math.floor(x), Y = Math.floor(y), Z = Math.floor(z);
    x -= X; y -= Y; z -= Z; X &= 255; Y &= 255; Z &= 255;
    const u = fade(x), v = fade(y), w = fade(z), p = this.p;
    const A = p[X] + Y, AA = p[A] + Z, AB = p[A + 1] + Z, B = p[X + 1] + Y, BA = p[B] + Z, BB = p[B + 1] + Z;
    const x1 = x - 1, y1 = y - 1, z1 = z - 1;
    const a0 = grad(p[AA], x, y, z), a1 = grad(p[BA], x1, y, z), a2 = grad(p[AB], x, y1, z), a3 = grad(p[BB], x1, y1, z);
    const b0 = grad(p[AA + 1], x, y, z1), b1 = grad(p[BA + 1], x1, y, z1), b2 = grad(p[AB + 1], x, y1, z1), b3 = grad(p[BB + 1], x1, y1, z1);
    const la = a0 + u * (a1 - a0), lb = a2 + u * (a3 - a2), lc = b0 + u * (b1 - b0), ld = b2 + u * (b3 - b2);
    const m0 = la + v * (lb - la), m1 = lc + v * (ld - lc);
    return m0 + w * (m1 - m0);
  }
  /** Gradient noise that repeats every `per` lattice cells on each axis (a tiling volume is built from this). */
  nP(x: number, y: number, z: number, per: number): number {
    const fx = Math.floor(x), fy = Math.floor(y), fz = Math.floor(z);
    x -= fx; y -= fy; z -= fz;
    const X = ((fx % per) + per) % per, Y = ((fy % per) + per) % per, Z = ((fz % per) + per) % per;
    const X1 = (X + 1) % per, Y1 = (Y + 1) % per, Z1 = (Z + 1) % per;
    const u = fade(x), v = fade(y), w = fade(z), p = this.p;
    const h = (a: number, b: number, c: number): number => p[p[p[a] + b] + c];
    const x1 = x - 1, y1 = y - 1, z1 = z - 1;
    const la = grad(h(X, Y, Z), x, y, z), lb = grad(h(X1, Y, Z), x1, y, z), lc = grad(h(X, Y1, Z), x, y1, z), ld = grad(h(X1, Y1, Z), x1, y1, z);
    const le = grad(h(X, Y, Z1), x, y, z1), lf = grad(h(X1, Y, Z1), x1, y, z1), lg = grad(h(X, Y1, Z1), x, y1, z1), lh = grad(h(X1, Y1, Z1), x1, y1, z1);
    const m0 = la + u * (lb - la), m1 = lc + u * (ld - lc), m2 = le + u * (lf - le), m3 = lg + u * (lh - lg);
    const n0 = m0 + v * (m1 - m0), n1 = m2 + v * (m3 - m2);
    return n0 + w * (n1 - n0);
  }
  /** Fractal sum, about −1..1 (normalised by the amplitudes). */
  fbm(x: number, y: number, z: number, oct: number, gain = 0.5): number {
    let a = 1, f = 1, s = 0, k = 0;
    for (let i = 0; i < oct; i++) { s += a * this.n(x * f, y * f, z * f); k += a; a *= gain; f *= 2.03; }
    return s / k;
  }
  /** Ridged fractal sum 0..1: thin bright creases where the noise crosses zero — filaments. */
  ridge(x: number, y: number, z: number, oct: number, gain = 0.5): number {
    let a = 1, f = 1, s = 0, k = 0;
    for (let i = 0; i < oct; i++) { const v = 1 - Math.abs(this.n(x * f, y * f, z * f)); s += a * v * v; k += a; a *= gain; f *= 2.07; }
    return s / k;
  }
}

const sat = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);
/** GLSL smoothstep, either direction (e0 > e1 falls). */
export function nebulaSmooth(e0: number, e1: number, x: number): number {
  const t = sat((x - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
}
const sq = (x: number): number => x * x;
function norm3(v: NebulaV3): NebulaV3 { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; }
/** A unit vector from two uniform numbers (uniform on the sphere). */
function unitFrom(a: number, b: number): NebulaV3 { const z = 2 * a - 1, r = Math.sqrt(Math.max(0, 1 - z * z)), t = 2 * Math.PI * b; return [r * Math.cos(t), z, r * Math.sin(t)]; }

/** The six axis permutations a sheet may read its wrinkles through (and the march's mirror of them). */
export const NEBULA_PERMS: ReadonlyArray<readonly [number, number, number, number, number, number]> = [
  // [srcX, srcY, srcZ, signX, signY, signZ]
  [0, 1, 2, 1, 1, 1], [1, 2, 0, 1, 1, 1], [2, 0, 1, 1, 1, 1], [1, 0, 2, -1, 1, 1], [0, 2, 1, 1, -1, 1], [2, 1, 0, 1, 1, -1],
];

// ─── the presets ─────────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * Line colours in linear sRGB (max channel 1) as an NebulaRGB camera renders them — measured on broadband photographs, see
 * atlas/docs/research/2026-10-07-space-realism/references-nebula.md. Hα + Hβ + Hγ at the case-B ratios is a rose red,
 * [O III] 495.9 / 500.7 a teal; dust-scattered light is bluer than its star.
 */
export const NEBULA_COLORS = {
  hAlpha: [1.0, 0.17, 0.29] as NebulaRGB,
  oIII: [0.1, 0.9, 0.72] as NebulaRGB,
  nII: [1.0, 0.1, 0.06] as NebulaRGB,
  scatter: [0.55, 0.72, 1.0] as NebulaRGB,
  /** A_λ/A_V at 650 / 550 / 450 nm for R_V = 3.1 (Cardelli, Clayton & Mathis 1989): dust reddens what it dims. */
  redden: [0.83, 1.0, 1.29] as NebulaRGB,
} as const;

/** A complete spec for a kind and a seed. Everything a caller may want to change afterwards is a plain field. */
export function nebulaPreset(kind: NebulaKind, seed: number): NebulaSpec {
  const r = mulberry32((seed >>> 0) ^ 0x9e3779b9);
  const u = (): number => r();
  const C = NEBULA_COLORS;
  const base: NebulaSpec = {
    kind, seed, light: null,
    colLow: [...C.hAlpha], colHigh: [...C.oIII], emission: 18, highGain: 0.9, hiR0: 0.2, hiR1: 0.55, hiPow: 3,
    starColor: [0.82, 0.9, 1.0], starPower: 1, colScatter: [...C.scatter], scatter: 0.08, phaseG: 0.55, lightPow: 0.4,
    ambient: [0.006, 0.0055, 0.005], kappa: 14, redden: [...C.redden], gasKappa: 0.25, kIon: 22, kIonDust: 10,
    sheets: [], stars: [], detail: 0.55, detailFreq: 5.5, shape: {},
  };
  if (kind === 'emission') {
    const wall = unitFrom(u(), u());
    const star: NebulaV3 = [(u() - 0.5) * 0.16 - wall[0] * 0.08, (u() - 0.5) * 0.12 - wall[1] * 0.08, (u() - 0.5) * 0.16 - wall[2] * 0.08];
    base.light = { mode: 'point', pos: star };
    base.starColor = [0.74, 0.86, 1.0]; base.starPower = 1.4;
    // TRUE COLOUR: [O III] only whitens the middle (photographs: core (170,137,157), saturation 0.19 against 0.29 further
    // out). A saturated teal core is the mark of a narrowband palette — the planetary keeps the teal, an H II region does not.
    base.colHigh = [1.0, 0.8, 0.92]; base.highGain = 1.3; base.hiR0 = 0.22; base.hiR1 = 0.7; base.hiPow = 2;
    // the glow reaches across most of the nebula, falling 5–30× to its outskirts (a bright knot in a dark cloud measured
    // core / envelope 86× against 16× in photographs): the thin side of the cloud is density-bounded — the light runs
    // through it — while the thick wall still stops it. Starlight scattered by the dust pales the body toward mauve.
    base.kIon = 17; base.scatter = 0.13;
    base.stars = [
      { pos: star, color: [0.78, 0.88, 1.0], power: 36 },
      { pos: [star[0] + 0.045, star[1] - 0.02, star[2] + 0.03], color: [0.85, 0.9, 1.0], power: 14 },
      { pos: [star[0] - 0.03, star[1] + 0.035, star[2] - 0.04], color: [1.0, 0.9, 0.78], power: 9 },
    ];
    base.shape = { wallX: wall[0], wallY: wall[1], wallZ: wall[2], r0: 0.24 + u() * 0.07, pillars: 4, lanes: 1, envW: 0.36 };
  } else if (kind === 'reflection') {
    const a = unitFrom(u(), u());
    const star: NebulaV3 = [(u() - 0.5) * 0.5, (u() - 0.5) * 0.3, (u() - 0.5) * 0.5];
    base.light = { mode: 'point', pos: star };
    base.emission = 0; base.scatter = 0.15; base.kappa = 6; base.kIon = 0; base.kIonDust = 3.2; base.lightPow = 1;
    base.ambient = [0.0105, 0.0088, 0.0092]; // the dust away from the star: neutral, a touch warm ((61,55,59) in photographs), never blue
    base.starColor = [0.72, 0.84, 1.0]; base.starPower = 1.6; base.phaseG = 0.52;
    // the scattered light is the STAR's colour made a little bluer (cross-section ∝ λ^−1.2; measured core (128,147,169),
    // saturation 0.24) — the first cut was Rayleigh blue, which no reflection nebula is
    base.colScatter = [0.6, 0.74, 1.0]; // measured after the first cut at (0.5, 0.64, 1): still B/G 1.7–2.1 displayed against 1.2–1.45
    base.stars = [
      { pos: star, color: [0.8, 0.88, 1.0], power: 44 },
      { pos: [-star[0] * 0.7 + 0.1, star[1] + 0.22, -star[2] * 0.5], color: [0.82, 0.9, 1.0], power: 18 },
    ];
    base.shape = { axX: a[0], axY: a[1], axZ: a[2], streak: 0.74 };
  } else if (kind === 'dark') {
    const L = unitFrom(u(), u());
    base.light = { mode: 'dir', pos: L };
    base.emission = 6; base.highGain = 0.2; base.hiR0 = 0; base.hiR1 = 0.01; // the backdrop is plain Hα
    base.kappa = 16; base.kIon = 3; base.kIonDust = 16; base.scatter = 0.03; base.starColor = [0.9, 0.92, 1.0];
    base.detail = 0.4;
    const a = unitFrom(u(), u());
    base.shape = { cores: 4, axX: a[0], axY: a[1], axZ: a[2], stretch: 0.6 };
  } else if (kind === 'planetary') {
    base.light = { mode: 'point', pos: [0, 0, 0] };
    // the ionisation ladder across the ring: blue-green [O III] (+ Hβ, He II) inside, red Hα + [N II] outside, the change
    // at 0.4–0.6 of the outer radius (photographs: inner R/G 0.6, B/G 1.37; rim R/G 2.1)
    base.emission = 1.6; base.highGain = 1.0; base.hiR0 = 0.36; base.hiR1 = 0.46; base.hiPow = 1;
    base.kIon = 0.6; base.kIonDust = 2; base.kappa = 2.2; base.scatter = 0.1; base.gasKappa = 0.1;
    base.colLow = [1.0, 0.2, 0.13];   // Hα + [N II]: an orange-red, deeper than an H II region's rose
    base.colHigh = [0.16, 0.76, 0.95]; // [O III] + Hβ + He II: a blue-green, bluer than [O III] alone
    base.stars = [{ pos: [0, 0, 0], color: [0.72, 0.84, 1.0], power: 16 }];
    // a near-round shell with its dense TORUS round the waist of a seeded axis: a ring seen down that axis (the Ring), an
    // ellipse at a slant (a ring's axis ratio is the cosine of its tilt — 0.74 in photographs), a bar with faint lobes
    // from the side (the Dumbbell). The shell itself stays near round so "distance from the star" means the same
    // thing in every direction: the colour zones are gated on it.
    const ax: NebulaV3 = [1, 0.9 + u() * 0.07, 0.92 + u() * 0.06];
    base.sheets = [
      // the ring's outer edge: one crisp front (the first cut drew two pencil outlines round a flat disc — a target, not a nebula)
      { axes: ax, radius: 0.56, amp: 0.035, width: 0.02, split: 0.0, perm: 0, colorOut: [1.0, 0.16, 0.12], colorIn: [1.0, 0.16, 0.12], power: 0.0004, maskLo: 0, maskHi: 0, sharpen: 1.2 },
      // the halo: ~1/1000 of the ring — there when the ring clips, otherwise not
      { axes: ax, radius: 0.76, amp: 0.06, width: 0.03, split: 0.0, perm: 1, colorOut: [1.0, 0.16, 0.14], colorIn: [1.0, 0.16, 0.14], power: 0.00016, maskLo: 0, maskHi: 0, sharpen: 1.2 },
    ];
    base.detail = 0.35; base.detailFreq = 7;
    const tor = unitFrom(u(), u()); // the torus' axis: every planetary is tilted its own way
    base.shape = { ax: ax[0], ay: ax[1], az: ax[2], axX: tor[0], axY: tor[1], axZ: tor[2] };
  } else {
    // remnant: light = none (a shock makes its own light), no dust to speak of, three wrinkled shells
    base.emission = 0.03; base.highGain = 1; base.hiR0 = 0; base.hiR1 = 0.01; base.kappa = 1; base.gasKappa = 0.02; base.detail = 0.3; base.detailFreq = 3.1;
    base.colLow = [0.55, 0.22, 0.3];
    const red: NebulaRGB = [1.0, 0.17, 0.2], teal: NebulaRGB = [0.14, 0.82, 0.86];
    // thin (a sheet's edge-on gain over its face-on glow goes as √(curvature radius / width)) and patchy: a remnant
    // radiates only where its blast wave has met dense gas
    // deeply corrugated (a band of folds near the limb is a BUNDLE of filaments, not one line) and each strand its
    // own colour: the [O III] and the Hα of a real remnant run side by side, they do not sum to white
    const off: NebulaRGB = [0, 0, 0];
    base.sheets = [
      { axes: [1, 1, 1], radius: 0.74, amp: 0.24, width: 0.0006, split: 0.012, perm: 0, colorOut: teal, colorIn: red, power: 0.016, maskLo: 0.45, maskHi: 0.75, sharpen: 1.8 },
      { axes: [1, 0.94, 1.04], radius: 0.66, amp: 0.27, width: 0.0007, split: 0, perm: 3, colorOut: red, colorIn: off, power: 0.02, maskLo: 0.5, maskHi: 0.8, sharpen: 1.8 },
      { axes: [1.05, 1, 0.95], radius: 0.57, amp: 0.3, width: 0.0008, split: 0, perm: 5, colorOut: teal, colorIn: off, power: 0.015, maskLo: 0.55, maskHi: 0.85, sharpen: 1.8 },
    ];
    const ra = unitFrom(u(), u());
    base.shape = { axX: ra[0], axY: ra[1], axZ: ra[2] };
  }
  return base;
}

// ─── the field ───────────────────────────────────────────────────────────────────────────────────────────────────────

/** What `nebulaFieldAt` fills per point: gas, dust, h. */
export interface NebulaFieldOut { gas: number; dust: number; h: number }

/** Everything the field needs that is derived once from the spec (noise tables, the seeded directions). */
export interface NebulaFieldCtx { spec: NebulaSpec; N: NebulaNoise; dirs: NebulaV3[]; amps: number[]; widths: number[]; cores: Array<{ c: NebulaV3; r: number }>; wall: NebulaV3; ax: NebulaV3 }

export function nebulaFieldContext(spec: NebulaSpec): NebulaFieldCtx {
  const N = new NebulaNoise(spec.seed);
  const r = mulberry32((spec.seed >>> 0) ^ 0x51ed270b);
  const sh = spec.shape;
  const wall = norm3([sh.wallX ?? 0, sh.wallY ?? -1, sh.wallZ ?? 0]);
  const dirs: NebulaV3[] = [], amps: number[] = [], widths: number[] = [];
  const K = Math.round(sh.pillars ?? 0);
  for (let i = 0; i < K; i++) {
    // fingers grow from the dense side of the cloud: directions within ~65° of the wall's
    const d = unitFrom(r(), r());
    const t = 0.55 + 0.4 * r();
    dirs.push(norm3([d[0] * (1 - t) + wall[0] * t, d[1] * (1 - t) + wall[1] * t, d[2] * (1 - t) + wall[2] * t]));
    amps.push(0.42 + 0.3 * r());
    widths.push(0.006 + 0.012 * r());
  }
  const cores: Array<{ c: NebulaV3; r: number }> = [];
  const nc = Math.round(sh.cores ?? 0);
  for (let i = 0; i < nc; i++) cores.push({ c: [(r() - 0.5) * 0.62, (r() - 0.5) * 0.44, (r() - 0.5) * 0.62], r: 0.2 + 0.18 * r() });
  return { spec, N, dirs, amps, widths, cores, wall, ax: norm3([sh.axX ?? 1, sh.axY ?? 0, sh.axZ ?? 0]) };
}

/** The nebula's gas, dust and wrinkle field at a unit-sphere point. */
export function nebulaFieldAt(c: NebulaFieldCtx, x: number, y: number, z: number, out: NebulaFieldOut): void {
  const N = c.N, spec = c.spec, sh = spec.shape;
  const r = Math.hypot(x, y, z);
  out.gas = 0; out.dust = 0; out.h = 0;
  if (r >= 1) return;
  // nothing may be cut by the bounding sphere — and nothing may trace it either: the outline is a lumpy surface between
  // 0.58 and 0.97 radii, by direction (a nebula that fills its sphere reads as a ball at once)
  const ir0 = 1 / (r || 1e-6);
  const edge = 0.58 + 0.39 * sat(0.5 + 1.1 * N.fbm(x * ir0 * 1.35 + 40.1, y * ir0 * 1.35 + 17.3, z * ir0 * 1.35 + 63.7, 3, 0.55));
  const env = nebulaSmooth(edge, edge - (sh.envW ?? 0.24), r);
  if (spec.kind === 'emission') {
    const wx = N.fbm(x * 1.1 + 11.3, y * 1.1, z * 1.1, 3), wy = N.fbm(x * 1.1, y * 1.1 + 23.7, z * 1.1, 3), wz = N.fbm(x * 1.1, y * 1.1, z * 1.1 + 5.9, 3);
    const qx = x + 0.3 * wx, qy = y + 0.3 * wy, qz = z + 0.3 * wz;
    const cloud = N.fbm(qx * 2.1, qy * 2.1, qz * 2.1, 5, 0.55);
    // the molecular cloud: clumpy, and thick on one side only (a blister: the cavity has broken out of the other)
    const side = nebulaSmooth(-0.55, 0.45, x * c.wall[0] + y * c.wall[1] + z * c.wall[2] + 0.25 * wx);
    const M = sat(0.5 + 1.15 * cloud) * (0.14 + 0.86 * side);
    const s = spec.light!.pos;
    const dx = x - s[0], dy = y - s[1], dz = z - s[2];
    const rs = Math.hypot(dx, dy, dz) || 1e-6;
    const ux = dx / rs, uy = dy / rs, uz = dz / rs;
    // the cavity's radius by direction: irregular, and pulled in toward the star along each finger
    // (it breaks out on the cloud's thin side: a blister, wider there than toward the wall)
    const open = -(ux * c.wall[0] + uy * c.wall[1] + uz * c.wall[2]);
    let rc = (sh.r0 ?? 0.32) * (1 + 0.42 * N.fbm(ux * 1.7 + 5.1, uy * 1.7 + 9.2, uz * 1.7 + 2.3, 3)) * (1 + 0.45 * nebulaSmooth(-0.2, 0.9, open));
    let fing = 0;
    for (let k = 0; k < c.dirs.length; k++) {
      const d = c.dirs[k];
      const f = c.amps[k] * Math.exp(-(1 - (ux * d[0] + uy * d[1] + uz * d[2])) / c.widths[k]);
      if (f > fing) fing = f;
    }
    rc *= 1 - fing;
    const t = rs / rc;
    const inside = nebulaSmooth(0.9, 1.05, t);             // 0 in the cavity → 1 in the cloud
    const shell = Math.exp(-sq((t - 1.06) / 0.1));   // the swept-up wall
    // wisps: the cloud is combed into filaments at a finer scale than its clumps
    const wisp = 0.55 + 0.9 * N.ridge(qx * 4.3 + 12.2, qy * 4.3 + 30.5, qz * 4.3 + 7.1, 3);
    let gas = 0.13 * (0.65 + 0.35 * cloud) * Math.exp(-rs / 0.45) * (1 - inside) + inside * (0.22 + 0.78 * M) * 0.85 * wisp + 0.5 * shell * (0.3 + 0.7 * M) * wisp;
    // a finger's head is dense (it is what survived): thicken the gas where the cavity was pulled in
    gas += 0.5 * fing * inside;
    // dust lanes: thin dense filaments, some of them in front of the glow
    const lane = Math.pow(N.ridge(qx * 1.9 + 31.1, qy * 1.9 + 7.3, qz * 1.9 + 19.9, 2, 0.4), 9) * nebulaSmooth(-0.05, 0.4, N.n(x * 0.9 + 3.3, y * 0.9 + 1.7, z * 0.9 + 8.1) + 0.12) * (sh.lanes ?? 1);
    out.gas = env * sat(gas + 0.5 * lane);
    out.dust = env * sat(inside * M * M * 1.1 + 0.45 * fing * inside + 1.5 * lane);
  } else if (spec.kind === 'reflection') {
    // streaks: the field is stretched along one axis (dust combed by a magnetic field — the Pleiades' striations)
    const a = c.ax, st = sh.streak ?? 0.72, pa = x * a[0] + y * a[1] + z * a[2];
    const sx = x - a[0] * pa * st, sy = y - a[1] * pa * st, sz = z - a[2] * pa * st;
    const w = N.fbm(x * 1.3 + 4.4, y * 1.3 + 8.8, z * 1.3 + 1.2, 2);
    const cl = N.fbm(sx * 2.6 + 0.35 * w, sy * 2.6 + 0.35 * w, sz * 2.6 - 0.35 * w, 5, 0.55);
    const wisps = Math.pow(N.ridge(sx * 3.6 + 17.7, sy * 3.6 + 3.1, sz * 3.6 + 9.4, 3), 5);
    const blob = nebulaSmooth(-0.32, 0.38, N.fbm(x * 0.9 + 13.1, y * 0.9 + 6.6, z * 0.9 + 2.9, 2) + 0.18);
    // away from the star the cloud thickens: the lit hollow sits in dark dust (the Iris: a blue flower in a brown cloud)
    const lp = spec.light!.pos, ds = Math.hypot(x - lp[0], y - lp[1], z - lp[2]);
    const outer = nebulaSmooth(0.25, 0.7, ds) * Math.pow(sat(0.1 + 1.5 * N.fbm(x * 1.7 + 9.4, y * 1.7 + 2.2, z * 1.7 + 14.6, 4, 0.55)), 1.5);
    // near the star the dust is WISPS (thin streaked sheets with clear air between them), not a fog
    const lit = Math.pow(sat(0.12 + 1.3 * cl), 3) * 0.5 + 1.25 * wisps * sat(0.2 + 1.2 * cl);
    out.dust = env * sat(blob * lit + 0.8 * outer);
  } else if (spec.kind === 'dark') {
    const wx = N.fbm(x * 1.2 + 7.7, y * 1.2, z * 1.2, 3), wy = N.fbm(x * 1.2, y * 1.2 + 3.1, z * 1.2, 3), wz = N.fbm(x * 1.2, y * 1.2, z * 1.2 + 9.9, 3);
    const qx = x + 0.28 * wx, qy = y + 0.28 * wy, qz = z + 0.28 * wz;
    let f = -1;
    // the cores are drawn out along one axis (a cloud sheared by the flow it sits in): distance counts less along it
    const st = sh.stretch ?? 0;
    for (const k of c.cores) {
      const ex = qx - k.c[0], ey = qy - k.c[1], ez = qz - k.c[2], al = ex * c.ax[0] + ey * c.ax[1] + ez * c.ax[2];
      const v = 1 - Math.sqrt(Math.max(0, ex * ex + ey * ey + ez * ez - st * al * al)) / k.r;
      if (v > f) f = v;
    }
    const fv = f + 0.3 * N.fbm(qx * 2.4 + 1.1, qy * 2.4 + 2.2, qz * 2.4 + 3.3, 4, 0.55);
    const core = nebulaSmooth(-0.08, 0.16, fv);
    const tails = Math.pow(N.ridge(qx * 2.3 + 21.2, qy * 2.3 + 5.5, qz * 2.3 + 12.8, 3), 4) * sat(0.55 + 0.9 * N.fbm(x * 1.1 + 6.1, y * 1.1 + 4.2, z * 1.1 + 0.7, 2));
    // ragged: streamers trail off the body (a dark nebula's edge is torn, never a clean blob)
    const dust = sat(core + 0.9 * tails);
    // THE SKIN: where the star's light meets the cloud its gas boils off and glows — a bright-rimmed cloud. The baked
    // light keeps it to the lit side; seen along the surface (the limb) it is a bright rim, face-on a dull red wash.
    // (A broad veil thick on the star's side drew the whole nebula as a red ball from that side — the first in-sector
    // picture of The Murk, 2026-10-07.)
    // (broken, too: a front is brightest where the cloud is densest behind it — an even rim reads as a cell wall)
    const skin = 0.38 * (0.25 + 0.75 * sat(0.5 + 1.4 * N.fbm(x * 2.1 + 3.9, y * 2.1 + 12.4, z * 2.1 + 7.5, 3, 0.55))) * Math.exp(-sq((fv + 0.12) / 0.13));
    // thin gas around it, in patches with clear sky between: what the silhouette is seen against, never a fog
    const veil = 0.1 * Math.pow(sat(0.3 + 1.5 * N.fbm(x * 1.5 + 15.5, y * 1.5 + 2.4, z * 1.5 + 8.6, 4, 0.55)), 1.5);
    out.dust = env * dust;
    out.gas = env * sat(veil + skin);
  } else if (spec.kind === 'planetary') {
    const ex = x / (sh.ax ?? 1), ey = y / (sh.ay ?? 0.85), ez = z / (sh.az ?? 0.9);
    const re = Math.hypot(ex, ey, ez), ir = 1 / (re || 1e-6);
    const ux = ex * ir, uy = ey * ir, uz = ez * ir;
    const n1 = N.fbm(ux * 2.2 + re * 3 + 4.1, uy * 2.2 + 7.2, uz * 2.2 + 1.3, 3);
    // radial streaks: a function of DIRECTION, so they point at the star (the Helix's cometary knots)
    const spokes = Math.pow(N.ridge(ux * 6.5 + 2.2, uy * 6.5 + 8.1, uz * 6.5 + 5.7, 2), 2);
    // THE RING IS THICK and brighter than its middle (inner / ring 0.43 in photographs): a shell steep on the inside
    // (cometary knots roughen that edge) and soft on the outside, densest round the waist of the long axis (ux)
    // (a NARROW band: a broad one filled the whole disc with the ring's red from every side but the axis)
    const ua = ux * c.ax[0] + uy * c.ax[1] + uz * c.ax[2];
    const waist = 0.05 + 0.95 * Math.pow(1 - ua * ua, 4);
    const knots = Math.pow(N.ridge(ux * 6.5 + 2.2, uy * 6.5 + 8.1, uz * 6.5 + 5.7, 2), 1.5);
    const dr = re - 0.45;
    const main = Math.exp(-sq(dr / (dr < 0 ? 0.04 + 0.016 * knots : 0.08))) * (0.74 + 0.18 * n1 + 0.08 * knots) * waist;
    // the middle is thin gas, all of it high-ionisation: seen through the red caps it must still read blue-green
    // (photographs: R/G 0.6 over the inner 40 % of the radius), so it outweighs them — the ring is dense, not the caps
    const fill = 0.165 * nebulaSmooth(0.46, 0.2, re) * (0.8 + 0.2 * n1);
    const halo = 0.06 * Math.exp(-sq((re - 0.7) / 0.1)) * (0.35 + 0.65 * spokes);
    out.gas = env * sat(main + fill + halo);
    out.dust = env * 0.3 * halo * spokes;
    out.h = N.fbm(x * 3.1 + 9.9, y * 3.1 + 1.4, z * 3.1 + 6.6, 3, 0.5);
  } else {
    // remnant: the gas channel is the PATCHINESS the sheets show through (a remnant is arcs, never a whole bubble) and
    // a faint diffuse glow behind the shock
    const patch = sat(0.34 + 1.9 * N.fbm(x * 0.95 + 2.7, y * 0.95 + 11.1, z * 0.95 + 6.2, 2));
    out.gas = nebulaSmooth(0.3, 0.45, r) * nebulaSmooth(0.995, 0.93, r) * patch;
    // the wrinkles run ALONG one axis (a rippled curtain — Hester 1987): its folds are long parallel threads
    const a = c.ax, pa = x * a[0] + y * a[1] + z * a[2];
    const sx = x - a[0] * pa * 0.72, sy = y - a[1] * pa * 0.72, sz = z - a[2] * pa * 0.72;
    out.h = N.fbm(sx * 3.6 + 1.9, sy * 3.6 + 4.6, sz * 3.6 + 8.3, 3, 0.55);
  }
}

// ─── the bake ────────────────────────────────────────────────────────────────────────────────────────────────────────

const f32 = new Float32Array(1), u32 = new Uint32Array(f32.buffer);
/** IEEE 754 binary32 → binary16 bits (round to nearest; values here are small and finite). */
export function nebulaToHalf(v: number): number {
  f32[0] = v;
  const x = u32[0], sign = (x >>> 16) & 0x8000, mant = x & 0x7fffff;
  const exp = ((x >>> 23) & 0xff) - 112;
  if (exp <= 0) return exp < -10 ? sign : sign | (((mant | 0x800000) >> (1 - exp)) + 0x1000 >> 13);
  if (exp >= 31) return sign | 0x7c00;
  return sign + (exp << 10) + ((mant + 0x1000) >> 13);
}

function octDecode(u: number, v: number): NebulaV3 {
  const fx = u * 2 - 1, fy = v * 2 - 1;
  let x = fx, z = fy;
  const y = 1 - Math.abs(fx) - Math.abs(fy);
  if (y < 0) { const ox = (1 - Math.abs(x)) * (x >= 0 ? 1 : -1), oz = (1 - Math.abs(z)) * (z >= 0 ? 1 : -1); x = ox; z = oz; }
  return norm3([x, y, z]);
}
function octEncode(x: number, y: number, z: number): [number, number] {
  const s = Math.abs(x) + Math.abs(y) + Math.abs(z) || 1;
  let ox = x / s, oz = z / s;
  if (y < 0) { const tx = (1 - Math.abs(oz)) * (ox >= 0 ? 1 : -1), tz = (1 - Math.abs(ox)) * (oz >= 0 ? 1 : -1); ox = tx; oz = tz; }
  return [ox * 0.5 + 0.5, oz * 0.5 + 0.5];
}

/** Trilinear read of one channel of a baked volume at a unit-sphere point (texel centres at −1 + (i + ½)·2/n). */
export function sampleNebulaVolume(data: Float32Array, n: number, x: number, y: number, z: number, ch: number): number {
  let fx = (x * 0.5 + 0.5) * n - 0.5, fy = (y * 0.5 + 0.5) * n - 0.5, fz = (z * 0.5 + 0.5) * n - 0.5;
  const m = n - 1;
  fx = fx < 0 ? 0 : fx > m ? m : fx; fy = fy < 0 ? 0 : fy > m ? m : fy; fz = fz < 0 ? 0 : fz > m ? m : fz;
  const x0 = Math.floor(fx), y0 = Math.floor(fy), z0 = Math.floor(fz);
  const x1 = x0 < m ? x0 + 1 : m, y1 = y0 < m ? y0 + 1 : m, z1 = z0 < m ? z0 + 1 : m;
  const tx = fx - x0, ty = fy - y0, tz = fz - z0;
  const i = (xi: number, yi: number, zi: number): number => data[((zi * n + yi) * n + xi) * 4 + ch];
  const a = i(x0, y0, z0) + tx * (i(x1, y0, z0) - i(x0, y0, z0)), b = i(x0, y1, z0) + tx * (i(x1, y1, z0) - i(x0, y1, z0));
  const c = i(x0, y0, z1) + tx * (i(x1, y0, z1) - i(x0, y0, z1)), d = i(x0, y1, z1) + tx * (i(x1, y1, z1) - i(x0, y1, z1));
  const e = a + ty * (b - a), f = c + ty * (d - c);
  return e + tz * (f - e);
}

export interface NebulaVolumeData {
  /** size³ × RGBA (gas, light, dust, h), x fastest. */
  data: Float32Array;
  size: number;
}

/** Bakes a spec into a volume in slices. `step` returns true when `volume` is ready. */
export class NebulaBaker {
  readonly size: number;
  readonly spec: NebulaSpec;
  readonly volume: NebulaVolumeData;
  /** 0..1 */
  progress = 0;
  /** ms of arithmetic spent so far. */
  spentMs = 0;
  private phase: 'field' | 'rays' | 'light' | 'done' = 'field';
  private cursor = 0;
  private readonly ctx: NebulaFieldCtx;
  private readonly D: number;
  private readonly NR: number;
  private tau: Float32Array | null = null;
  private tMax = 2;
  private e1: NebulaV3 = [1, 0, 0];
  private e2: NebulaV3 = [0, 0, 1];

  constructor(spec: NebulaSpec, size: number) {
    this.spec = spec;
    this.size = Math.max(8, Math.round(size));
    this.volume = { data: new Float32Array(this.size ** 3 * 4), size: this.size };
    this.ctx = nebulaFieldContext(spec);
    // the light table: directions × distance. The angular step matches a voxel at half the radius.
    this.D = Math.max(16, Math.min(160, Math.round(this.size * 1.34)));
    this.NR = this.size;
    if (spec.light?.mode === 'dir') {
      const L = norm3(spec.light.pos);
      const ref: NebulaV3 = Math.abs(L[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
      this.e1 = norm3([ref[1] * L[2] - ref[2] * L[1], ref[2] * L[0] - ref[0] * L[2], ref[0] * L[1] - ref[1] * L[0]]);
      this.e2 = [L[1] * this.e1[2] - L[2] * this.e1[1], L[2] * this.e1[0] - L[0] * this.e1[2], L[0] * this.e1[1] - L[1] * this.e1[0]];
      this.tMax = 2;
    } else if (spec.light) this.tMax = Math.hypot(spec.light.pos[0], spec.light.pos[1], spec.light.pos[2]) + 1;
  }

  get done(): boolean { return this.phase === 'done'; }

  /** Work for about `budgetMs`; true once the volume is complete. */
  step(budgetMs = 6): boolean {
    if (this.phase === 'done') return true;
    const t0 = performance.now();
    const n = this.size, data = this.volume.data, o: NebulaFieldOut = { gas: 0, dust: 0, h: 0 };
    while (performance.now() - t0 < budgetMs) {
      if (this.phase === 'field') {
        // one ROW per turn of the loop (a whole z slice of a 128³ volume is tens of ms)
        const row = this.cursor, z = Math.floor(row / n), y = row - z * n;
        const zc = -1 + (z + 0.5) * 2 / n, yc = -1 + (y + 0.5) * 2 / n;
        let i = row * n * 4;
        for (let x = 0; x < n; x++, i += 4) {
          nebulaFieldAt(this.ctx, -1 + (x + 0.5) * 2 / n, yc, zc, o);
          data[i] = o.gas; data[i + 1] = 1; data[i + 2] = o.dust; data[i + 3] = o.h;
        }
        if (++this.cursor >= n * n) { this.cursor = 0; this.phase = this.spec.light && (this.spec.kIon > 0 || this.spec.kIonDust > 0) ? 'rays' : 'done'; }
        this.progress = this.phase === 'done' ? 1 : 0.7 * this.cursor / (n * n);
      } else if (this.phase === 'rays') {
        if (!this.tau) this.tau = new Float32Array(this.D * this.D * this.NR);
        this.rayRow(this.cursor);
        if (++this.cursor >= this.D) { this.cursor = 0; this.phase = 'light'; }
        this.progress = 0.7 + 0.15 * this.cursor / this.D;
      } else if (this.phase === 'light') {
        this.lightSlice(this.cursor);
        if (++this.cursor >= n) { this.phase = 'done'; this.tau = null; }
        this.progress = this.phase === 'done' ? 1 : 0.85 + 0.15 * this.cursor / n;
      } else break;
    }
    this.spentMs += performance.now() - t0;
    return this.phase === 'done';
  }

  /** Finish now (tests, node). */
  finish(): NebulaVolumeData { while (!this.step(1000)) { /* keep going */ } return this.volume; }

  /** Optical depth along every ray of one row of the direction table. */
  private rayRow(row: number): void {
    const { D, NR, spec } = this, tau = this.tau!, data = this.volume.data, n = this.size;
    // a coarse volume takes a softer front: one that stays a few voxels thick, or the march dithers it into grain
    const soft = Math.min(1, this.size / 96);
    const dt = this.tMax / NR, kI = spec.kIon * soft, kD = spec.kIonDust * soft, L = spec.light!;
    for (let col = 0; col < D; col++) {
      let ox: number, oy: number, oz: number, dx: number, dy: number, dz: number;
      if (L.mode === 'point') {
        const d = octDecode((col + 0.5) / D, (row + 0.5) / D);
        ox = L.pos[0]; oy = L.pos[1]; oz = L.pos[2]; dx = d[0]; dy = d[1]; dz = d[2];
      } else {
        // parallel light: a grid across the ball, the ray enters at its star-side face
        const a = -1 + (col + 0.5) * 2 / D, b = -1 + (row + 0.5) * 2 / D, Ld = norm3(L.pos);
        ox = this.e1[0] * a + this.e2[0] * b + Ld[0]; oy = this.e1[1] * a + this.e2[1] * b + Ld[1]; oz = this.e1[2] * a + this.e2[2] * b + Ld[2];
        dx = -Ld[0]; dy = -Ld[1]; dz = -Ld[2];
      }
      let acc = 0;
      const base = (row * D + col) * NR;
      for (let k = 0; k < NR; k++) {
        const t = (k + 0.5) * dt, x = ox + dx * t, y = oy + dy * t, z = oz + dz * t;
        if (x * x + y * y + z * z < 1) acc += (kI * sampleNebulaVolume(data, n, x, y, z, 0) + kD * sampleNebulaVolume(data, n, x, y, z, 2)) * dt;
        tau[base + k] = acc;
      }
    }
  }

  /** The light channel of one z slice, from the direction table. */
  private lightSlice(z: number): void {
    const { D, NR, spec } = this, tau = this.tau!, data = this.volume.data, n = this.size, L = spec.light!;
    const zc = -1 + (z + 0.5) * 2 / n, Ld = L.mode === 'dir' ? norm3(L.pos) : L.pos;
    const at = (c: number, r: number, k: number): number => tau[(r * D + c) * NR + k];
    for (let y = 0; y < n; y++) {
      const yc = -1 + (y + 0.5) * 2 / n;
      let i = ((z * n + y) * n) * 4 + 1;
      for (let x = 0; x < n; x++, i += 4) {
        const xc = -1 + (x + 0.5) * 2 / n;
        let u: number, v: number, t: number;
        if (L.mode === 'point') {
          const dx = xc - Ld[0], dy = yc - Ld[1], dz = zc - Ld[2];
          t = Math.hypot(dx, dy, dz);
          [u, v] = octEncode(dx, dy, dz);
        } else {
          u = (xc * this.e1[0] + yc * this.e1[1] + zc * this.e1[2]) * 0.5 + 0.5;
          v = (xc * this.e2[0] + yc * this.e2[1] + zc * this.e2[2]) * 0.5 + 0.5;
          t = 1 - (xc * Ld[0] + yc * Ld[1] + zc * Ld[2]);
        }
        let fc = u * D - 0.5, fr = v * D - 0.5, fk = t / this.tMax * NR - 0.5;
        fc = fc < 0 ? 0 : fc > D - 1 ? D - 1 : fc; fr = fr < 0 ? 0 : fr > D - 1 ? D - 1 : fr; fk = fk < 0 ? 0 : fk > NR - 1 ? NR - 1 : fk;
        const c0 = Math.floor(fc), r0 = Math.floor(fr), k0 = Math.floor(fk);
        const c1 = Math.min(D - 1, c0 + 1), r1 = Math.min(D - 1, r0 + 1), k1 = Math.min(NR - 1, k0 + 1);
        const tc = fc - c0, tr = fr - r0, tk = fk - k0;
        const lo = (at(c0, r0, k0) * (1 - tc) + at(c1, r0, k0) * tc) * (1 - tr) + (at(c0, r1, k0) * (1 - tc) + at(c1, r1, k0) * tc) * tr;
        const hi = (at(c0, r0, k1) * (1 - tc) + at(c1, r0, k1) * tc) * (1 - tr) + (at(c0, r1, k1) * (1 - tc) + at(c1, r1, k1) * tc) * tr;
        data[i] = Math.exp(-(lo + (hi - lo) * tk));
      }
    }
  }
}

/** The volume as half floats (what the GPU texture holds). */
export function nebulaVolumeToHalf(v: NebulaVolumeData): Uint16Array {
  const out = new Uint16Array(v.data.length);
  for (let i = 0; i < out.length; i++) out[i] = nebulaToHalf(v.data[i]);
  return out;
}

/** How many noise periods the detail volume holds across its width. */
export const NEBULA_DETAIL_PERIODS = 4;

/** A small TILING detail volume (n³ RGBA8): rgb a wispy vector noise (the march warps its lookups by it), a an erosion value. */
export function bakeNebulaDetail(n = 32, seed = 7): Uint8Array {
  const N = new NebulaNoise(seed), out = new Uint8Array(n * n * n * 4), per = NEBULA_DETAIL_PERIODS;
  // octaves at exact doublings of a periodic lattice: the sum tiles
  const fbmP = (x: number, y: number, z: number, o: number): number => {
    let a = 1, f = 1, s = 0, k = 0;
    for (let i = 0; i < 3; i++) { s += a * N.nP(x * f + o, y * f + o * 0.37, z * f + o * 0.71, per * f); k += a; a *= 0.55; f *= 2; }
    return s / k;
  };
  let i = 0;
  for (let z = 0; z < n; z++) for (let y = 0; y < n; y++) for (let x = 0; x < n; x++, i += 4) {
    const px = (x + 0.5) / n * per, py = (y + 0.5) / n * per, pz = (z + 0.5) / n * per;
    out[i] = Math.round(255 * sat(0.5 + 0.9 * fbmP(px, py, pz, 0)));
    out[i + 1] = Math.round(255 * sat(0.5 + 0.9 * fbmP(px, py, pz, 8)));
    out[i + 2] = Math.round(255 * sat(0.5 + 0.9 * fbmP(px, py, pz, 16)));
    out[i + 3] = Math.round(255 * sat(1 - Math.abs(fbmP(px, py, pz, 24)) * 2.2));   // ridged: thin bright creases
  }
  return out;
}
